import React from 'react';
import styles from '../../../styles/ListingManagement.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import type { ListingClientType, MaritalStatus } from '../../../types/ListingTypes';
import { ClientForm, PROVINCES, MARITAL_STATUSES } from './ClientTile';

interface Props {
  clientTypes: ListingClientType[];
  clientTypeId: string;
  onClientTypeChange: (id: string) => void;
  form: ClientForm;
  onFormChange: (field: keyof ClientForm, value: string) => void;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: () => void;
}

const FIELD_LABELS: Partial<Record<keyof ClientForm, string>> = {
  streetNumber: 'Street Number',
  streetName: 'Street Name',
  postalCode: 'Postal Code',
};

const TEXT_FIELDS: (keyof ClientForm)[] = [
  'name', 'surname', 'email', 'cell',
  'streetNumber', 'streetName', 'city', 'postalCode',
];

const AddClientModal: React.FC<Props> = ({
  clientTypes, clientTypeId, onClientTypeChange,
  form, onFormChange,
  saving, error,
  onClose, onSubmit,
}) => (
  <div
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    onClick={onClose}
  >
    <div
      style={{ background: '#fff', borderRadius: 10, padding: 28, width: 480, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
      onClick={e => e.stopPropagation()}
    >
      <h4 style={{ margin: '0 0 18px', fontSize: '1.05rem', fontWeight: 700 }}>Add Client</h4>

      {/* Client Type selector */}
      {clientTypes.length > 0 && (
        <div className={styles.categoryRow} style={{ marginBottom: 14 }}>
          <label className={styles.categoryLabel}>Client Type</label>
          <div className={styles.categoryInputWrapper}>
            <select
              className={styles.categoryInput}
              value={clientTypeId}
              onChange={e => onClientTypeChange(e.target.value)}
            >
              {clientTypes.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Text fields */}
      {TEXT_FIELDS.map(field => (
        <div key={field} className={styles.categoryRow} style={{ marginBottom: 10 }}>
          <label className={styles.categoryLabel}>
            {FIELD_LABELS[field] ?? field.charAt(0).toUpperCase() + field.slice(1)}
          </label>
          <div className={styles.categoryInputWrapper}>
            <input
              className={styles.categoryInput}
              type={field === 'streetNumber' ? 'number' : 'text'}
              value={form[field]}
              onChange={e => onFormChange(field, e.target.value)}
            />
          </div>
        </div>
      ))}

      {/* Province */}
      <div className={styles.categoryRow} style={{ marginBottom: 10 }}>
        <label className={styles.categoryLabel}>Province</label>
        <div className={styles.categoryInputWrapper}>
          <select
            className={styles.categoryInput}
            value={form.province}
            onChange={e => onFormChange('province', e.target.value)}
          >
            <option value="">Select…</option>
            {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>

      {/* Marital Status */}
      <div className={styles.categoryRow} style={{ marginBottom: 10 }}>
        <label className={styles.categoryLabel}>Marital Status</label>
        <div className={styles.categoryInputWrapper}>
          <select
            className={styles.categoryInput}
            value={form.maritalStatus}
            onChange={e => onFormChange('maritalStatus', e.target.value as MaritalStatus | '')}
          >
            <option value="">Select…</option>
            {MARITAL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {error && <p style={{ color: '#dc3545', fontSize: '0.85rem', marginBottom: 10 }}>{error}</p>}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
        <button
          type="button"
          className={`${buttonStyles.button} ${buttonStyles.secondary}`}
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className={`${buttonStyles.button} ${buttonStyles.primary}`}
          onClick={onSubmit}
          disabled={saving}
        >
          {saving ? 'Adding…' : 'Add Client'}
        </button>
      </div>
    </div>
  </div>
);

export default AddClientModal;
